import {
  AUDIO_EXTS,
  CODE_EXTS,
  DOCX_EXTS,
  HTML_EXTS,
  IMAGE_EXTS,
  MARKDOWN_EXTS,
  PDF_EXTS,
  SPREADSHEET_EXTS,
  VIDEO_EXTS,
  extOf,
  type PreviewKind,
} from './preview-extensions'

/**
 * Strip MIME parameters (`; charset=utf-8`, `; boundary=...`) and
 * lowercase. RFC 7231 allows parameters on any media type; servers and
 * libmagic sometimes emit them (e.g. `text/markdown; charset=utf-8`),
 * and our dispatcher uses exact equality, so we normalise the input.
 */
function normalizeMediaType(contentType: string): string {
  const semi = contentType.indexOf(';')
  const bare = semi < 0 ? contentType : contentType.slice(0, semi)
  return bare.trim().toLowerCase()
}

/**
 * Shared renderer classification for user uploads, artifact cards and dialogs.
 *
 * Content-type-first because the server-side `infer_content_type`
 * (apps/api/routers/agent_api/_mime_inference.py) already runs libmagic
 * + `mimetypes.guess_type` and writes an authoritative MIME — that
 * signal is strictly stronger than re-sniffing the filename here. The
 * filename provides a fallback for missing or generic MIME. Plain text
 * can also use Markdown/CSV/TSV extensions; concrete MIME types win.
 *
 * Branch order is correctness-sensitive: specific MIME literals
 * (`text/csv`, `text/markdown`, `text/html`) MUST come before the
 * generic `text/*` -> `'code'` catch-all, otherwise CSV/Markdown/HTML
 * would all collapse into the code preview. The unit test pins this
 * ordering with explicit regression cases.
 *
 * Inputs are normalised to the bare media type (lowercased, parameters
 * stripped) before comparison so a parameterised header like
 * `text/markdown; charset=utf-8` still routes correctly.
 */
export function pickPreviewKindForAttachment(
  name: string,
  contentType: string | null | undefined,
): PreviewKind {
  const mediaType = normalizeMediaType(contentType ?? '')

  // 1) Specific MIME types — must precede the generic `text/*` branch.
  if (mediaType === 'application/pdf') return 'pdf'
  if (mediaType === 'text/markdown' || mediaType === 'text/x-markdown') {
    return 'markdown'
  }
  if (mediaType === 'text/html' || mediaType === 'application/xhtml+xml') {
    return 'html'
  }
  if (
    mediaType === 'text/csv' ||
    mediaType === 'text/tab-separated-values' ||
    mediaType.includes('spreadsheet') ||
    mediaType === 'application/vnd.ms-excel'
  ) {
    return 'spreadsheet'
  }
  if (
    mediaType ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'docx'
  }
  if (mediaType === 'application/json') return 'code'
  if (mediaType === 'application/xml') return 'code'

  // 2) Generic top-level MIME categories.
  if (mediaType.startsWith('image/')) return 'image'
  if (mediaType.startsWith('video/')) return 'video'
  if (mediaType.startsWith('audio/')) return 'audio'
  if (mediaType === 'text/plain') {
    // Plain text is a generic fallback for structured text uploads.
    // A concrete MIME (e.g. application/json) still wins above.
    const ext = extOf(name)
    if (MARKDOWN_EXTS.has(ext)) return 'markdown'
    if (ext === 'csv' || ext === 'tsv') return 'spreadsheet'
  }
  if (mediaType.startsWith('text/')) return 'code'

  // 3) Filename-extension fallback for the case where the server-side
  //    inference could not produce a useful content_type (typically
  //    `application/octet-stream`).
  const ext = extOf(name)
  if (IMAGE_EXTS.has(ext)) return 'image'
  if (VIDEO_EXTS.has(ext)) return 'video'
  if (AUDIO_EXTS.has(ext)) return 'audio'
  if (PDF_EXTS.has(ext)) return 'pdf'
  if (SPREADSHEET_EXTS.has(ext)) return 'spreadsheet'
  if (DOCX_EXTS.has(ext)) return 'docx'
  if (MARKDOWN_EXTS.has(ext)) return 'markdown'
  if (HTML_EXTS.has(ext)) return 'html'
  if (CODE_EXTS.has(ext)) return 'code'
  return 'fallback'
}
